import config from 'config'

import globalActions from 'actions'
import { push } from 'react-router-redux'

import { menuActivePeriodsFetcher } from '@library/menu-service'

import * as trackingKeys from 'actions/trackingKeys'
import { fetchData } from 'routes/Menu/fetchData'
import { fetchMenuV3 } from 'routes/Menu/fetchData/fetchMenuServiceV3'
import { getBasketRecipes, getBasketDate, getBasketUsageCount } from 'selectors/basket'
import { getTransactionType, getUTMAndPromoCode } from 'selectors/tracking'
import { getUserOrders } from 'selectors/user'
import { limitReached, naiveLimitReached } from 'utils/basket'
import { productCanBeAdded } from 'utils/basketProductLimits'
import { getUserOrderById } from 'utils/user'

import {
  basketChangesConfirmed,
  basketChangesModalClosed,
  basketChangesModalShown,
} from 'actions/trackingKeys'
import { locationQuery } from 'selectors/routing'
import { getIsAuthenticated } from '../selectors/auth'
import { actionTypes } from './actionTypes'
import { boxSummaryDeliveryDaysLoad } from './boxSummary'
import statusActions from './status'
import { trackingOrderCheckout } from './tracking'

export const basketIsPortionSizeViewVisibleChange = (value) => ({
  type: actionTypes.BASKET_IS_PORTION_SIZE_VIEW_VISIBLE,
  value,
})

export const basketIsShowDetailsViewFirstChange = (value) => ({
  type: actionTypes.BASKET_IS_SHOW_DETAILS_VIEW_FIRST,
  value,
})

export const basketOrderLoaded = (orderId) => (dispatch, getState) => {
  const editBox = getState().basket.get('recipes').size !== 0
  dispatch({
    type: actionTypes.BASKET_ORDER_LOADED,
    orderId,
    editBox,
  })
}

export const basketDateChange = (date) => ({
  type: actionTypes.BASKET_DATE_CHANGE,
  date,
})

export const basketGiftAdd =
  (giftId, type = '') =>
    (dispatch, getState) => {
      if (type.toLowerCase() === 'product') {
        if (getState().products.has(giftId)) {
          dispatch({
            type: actionTypes.BASKET_GIFT_ADD,
            giftId,
          })
        }
      }
    }

/**
 * Update the number of portions in the state and query (if already present in the query)
 * Dispatch action to notify if box limit reached after changing number of portions
 *
 * If your use case requires re-fetching the menu data, you can use `basketNumPortionChange` instead
 * @see basketNumPortionChange
 */
export const setBasketNumPortion = (numPortions, view) => (dispatch, getState) => {
  const state = getState()
  const { routing } = state
  const prevLoc = routing ? routing.locationBeforeTransitions : null
  const query = prevLoc.query || null

  if (query && query.num_portions) {
    const newLoc = {
      ...prevLoc,
      num_portions: numPortions,
    }

    dispatch(push(newLoc))
  }

  dispatch({
    type: actionTypes.BASKET_NUM_PORTION_CHANGE,
    numPortions,
  })

  const basketUsage = getBasketUsageCount(state)
  const reachedLimit = limitReached(basketUsage)

  dispatch({
    type: actionTypes.BASKET_LIMIT_REACHED,
    limitReached: reachedLimit,
    trackingData: {
      actionType: trackingKeys.basketLimit,
      source: trackingKeys.selectPortionSize,
      limitReached: reachedLimit,
    },
  })

  const { promoCode, UTM } = getUTMAndPromoCode(getState())

  dispatch({
    type: actionTypes.BOX_SIZE_CHANGED_TRACKING,
    trackingData: {
      actionType: trackingKeys.selectBoxSize,
      view,
      boxSize: numPortions,
      ...UTM,
      promoCode,
    },
  })
}

/**
 * Fetches the menu v3 data for the new number of portions and updates the state
 *
 * @see setBasketNumPortion
 */
export const basketNumPortionChange = (numPortions, view) => async (dispatch, getState) => {
  const deliveryDate = getBasketDate(getState())
  const query = locationQuery(getState())
  const isAuthenticated = getIsAuthenticated(getState())

  dispatch(globalActions.pending(actionTypes.MENU_FETCH_DATA, true))
  await dispatch(fetchMenuV3({
    deliveryDate,
    numPortions,
    includeParentCategories: isAuthenticated,
    includeCategoryMerchandising: true,
    'preview[menu_id]': query?.['preview[menu_id]'],
    'preview[auth_user_id]': query?.['preview[auth_user_id]'],
    'preview[expiry]': query?.['preview[expiry]'],
    'preview[signature]': query?.['preview[signature]'],
  }))
  dispatch(globalActions.pending(actionTypes.MENU_FETCH_DATA, false))

  dispatch(setBasketNumPortion(numPortions, view))
}

export const portionSizeSelectedTracking = (numPortion, orderId) => (dispatch) => {
  dispatch({
    type: actionTypes.PORTION_SIZE_SELECTED_TRACKING,
    trackingData: {
      actionType: trackingKeys.selectPortionSize,
      num_portion: numPortion,
      order_id: orderId || null,
    },
  })
}

export const basketReset = (chosenAddress = null) => ({
  type: actionTypes.BASKET_RESET,
  payload: {
    chosenAddress,
  },
})

export const basketIdChange = (orderId) => ({
  type: actionTypes.BASKET_ID_CHANGE,
  orderId,
})

export const basketProductAdd =
  (productId, view = null, force = false) =>
    (dispatch, getState) => {
      const product = getState().products.get(productId, false)

      if (product) {
        const state = getState()
        if (
          force ||
        productCanBeAdded(
          productId,
          state.basket,
          state.products,
          state.productsStock,
          state.productsCategories,
        )
        ) {
          dispatch({
            type: actionTypes.BASKET_PRODUCT_ADD,
            productId,
            unsaved: !force,
            trackingData: force
              ? undefined
              : {
                actionType: actionTypes.BASKET_PRODUCT_ADD,
                productId,
                view,
              },
          })

          if (!force) {
            dispatch({
              type: actionTypes.PRODUCTS_STOCK_CHANGE,
              stock: { [productId]: -1 },
            })
          }
        }
      }
    }

export const basketOrderItemsLoad =
  (
    orderId, addRecipe, order = null, types = ['product', 'recipe', 'gift'],
    view = null) =>
    (dispatch, getState) => {
      const userOrder = order ||
        getUserOrderById(orderId, getState().user.get('orders'))

      types.forEach((type) => {
        userOrder.get(`${type}Items`, []).forEach((item) => {
          const itemableId = item.get('itemableId')
          const qty = parseInt(item.get('quantity', 0), 10)
          switch (type) {
          case 'product': {
            for (let i = 0; i < qty; i++) {
              dispatch(basketProductAdd(itemableId, view, orderId))
            }
            break
          }
          case 'recipe': {
            const adjustedQty = Math.round(
              qty / parseInt(userOrder.getIn(['box', 'numPortions']), 10),
            )

            for (let i = 0; i < adjustedQty; i++) {
              // fall back to the defaults for these 2 params
              const recipeInfo = undefined

              addRecipe(itemableId, view, recipeInfo, orderId, true)
            }
            break
          }
          case 'gift': {
            const itemableType = item.get('itemableType')

            for (let i = 0; i < qty; i++) {
              dispatch(basketGiftAdd(itemableId, itemableType))
            }
            break
          }
          default:
            break
          }
        })
      })
    }

export const basketOrderLoad =
  (orderId, addRecipe, order = null) =>
    (dispatch, getState) => {
      if (getState().basket.get('orderId') !== orderId) {
        dispatch(basketReset())
        dispatch(basketIdChange(orderId))
        dispatch(basketOrderItemsLoad(orderId, addRecipe, order))
      }
      dispatch(basketOrderLoaded(orderId))
    }

export const basketProductRemove = (productId, view) => (
  dispatch, getState) => {
  const product = getState().products.get(productId, false)

  if (product) {
    const productProperties = product.delete('media')
      .delete('images')
      .delete('tags')
      .delete('categories')

    dispatch({
      type: actionTypes.BASKET_PRODUCT_REMOVE,
      productId,
      unsaved: true,
      trackingData: {
        actionType: actionTypes.BASKET_PRODUCT_REMOVE,
        productId,
        view,
        eventName: 'basket_market_product_removed',
        eventAction: 'clicked',
        eventType: 'primary_action',
        eventProperties: { productProperties },
      },
    })

    dispatch({
      type: actionTypes.PRODUCTS_STOCK_CHANGE,
      stock: { [productId]: 1 },
    })
  }
}

export const basketPromoCodeChange = (promoCode) => ({
  type: actionTypes.BASKET_PROMO_CODE_CHANGE,
  promoCode,
})

export const basketPromoCodeAppliedChange = (promoCodeApplied) => ({
  type: actionTypes.BASKET_PROMO_CODE_APPLIED_CHANGE,
  promoCodeApplied,
})

export const basketPromoCodeUrlChange = (promoCodeUrl) => ({
  type: actionTypes.BASKET_PROMO_CODE_URL_CHANGE,
  promoCodeUrl,
})

export const basketPostcodeChangePure = (postcode) => ({
  type: actionTypes.BASKET_POSTCODE_CHANGE,
  postcode: postcode.trim(),
})

export const getMenuCutoffUntil = async () => {
  const activePeriods = await menuActivePeriodsFetcher()
  const tertiaryPeriod = activePeriods.data[activePeriods.data.length - 1]

  return tertiaryPeriod.until
}

export const basketPostcodeChange =
  (postcode, forgetPrevPostcode = false) =>
    async (dispatch, getState) => {
      const trimmedPostcode = postcode.trim()

      if (postcode) {
        dispatch({
          type: actionTypes.BASKET_POSTCODE_CHANGE,
          postcode: trimmedPostcode,
          forgetPrevPostcode,
          trackingData: {
            actionType: trackingKeys.changeBasketPostcode,
            postcode: trimmedPostcode,
          },
        })
        dispatch({
          type: actionTypes.BASKET_POSTCODE_PENDING,
          pending: true,
        })

        await dispatch(
          boxSummaryDeliveryDaysLoad(
            undefined, // if any other value is passed, 1st delivery day would be offset 1 day back, resulting in bug
            await getMenuCutoffUntil(),
          ),
        )

        dispatch({
          type: actionTypes.BASKET_POSTCODE_PENDING,
          pending: false,
        })

        const { promoCode, UTM } = getUTMAndPromoCode(getState())
        dispatch({
          type: actionTypes.BASKET_SELECT_POSTCODE,
          trackingData: {
            actionType: trackingKeys.selectPostcode,
            ...UTM,
            promoCode,
            postcode: trimmedPostcode,
          },
        })
      }
    }

export const basketPostcodeClear = () => (dispatch) => {
  dispatch({
    type: actionTypes.BASKET_POSTCODE_CHANGE,
    postcode: '',
  })
  dispatch({
    type: actionTypes.BASKET_ADDRESS_CHANGE,
    address: null,
  })
}

export const basketAddressChange = (address) => ({
  type: actionTypes.BASKET_ADDRESS_CHANGE,
  address,
})

export const basketRecipesInitialise = (recipes) => (dispatch, getState) => {
  dispatch({
    type: actionTypes.BASKET_RECIPES_INITIALISE,
    recipes,
  })

  const { basket } = getState()
  const reachedLimit = naiveLimitReached(basket)
  dispatch({
    type: actionTypes.BASKET_LIMIT_REACHED,
    limitReached: reachedLimit,
  })
}

export const basketRecipePositionsInitialise = (recipePositions) => (dispatch) => {
  dispatch({
    type: actionTypes.BASKET_RECIPE_POSITIONS_INITIALISE,
    recipePositions,
  })
}

export const basketSlotChange = (slotId) => (dispatch, getState) => {
  const state = getState()
  const date = state.basket.get('date')
  const userOrders = getUserOrders(getState())
  const orderForDate = userOrders.find((order) => {
    const deliveryDay = order.get('deliveryDate').split(' ')[0]

    return deliveryDay === date
  })

  dispatch({
    type: actionTypes.BASKET_SLOT_CHANGE,
    slotId,
    trackingData: {
      actionType: trackingKeys.changeBasketSlot,
      slotId,
      date,
      dayId: state.boxSummaryDeliveryDays.getIn([date, 'id']),
    },
  })

  const orderId = orderForDate?.get('id')
  dispatch(basketIdChange(orderId))

  const slots = state.boxSummaryDeliveryDays.getIn([date, 'slots'], null)
  if (slots && slots.size > 0) {
    const selectedSlot = slots.find((slot) => slot.get('id') === slotId)
    const defaultDelivery =
      selectedSlot &&
      selectedSlot.get('isDefault') &&
      state.boxSummaryDeliveryDays.getIn([date, 'isDefault'])
    const { promoCode, UTM } = getUTMAndPromoCode(state)
    dispatch({
      type: actionTypes.BASKET_SELECT_DELIVERY_SLOT,
      trackingData: {
        actionType: trackingKeys.selectDeliverySlot,
        ...UTM,
        promoCode,
        deliverySlot: defaultDelivery ? 'default' : 'not default',
      },
    })
  }
}

export const basketPreviewOrderChange = (
  previewOrderId, boxId) => ({
  type: actionTypes.BASKET_PREVIEW_ORDER_CHANGE,
  boxId,
  previewOrderId,
})

export const basketSlotClear = () => ({
  type: actionTypes.BASKET_SLOT_CHANGE,
  slotId: '',
})

export const basketTariffChange = (tariffId) => ({
  type: actionTypes.BASKET_TARIFF_CHANGE,
  tariffId,
})

export const basketChosenAddressChange = (address) => ({
  type: actionTypes.BASKET_CHOSEN_ADDRESS_CHANGE,
  address,
})

export const basketRestorePreviousValues = () => (dispatch, getState) => {
  const { basket } = getState()
  const prevSlotId = basket.get('prevSlotId')
  const slotId = basket.get('slotId')
  const prevPostcode = basket.get('prevPostcode')
  const postcode = basket.get('postcode')
  const prevAddress = basket.get('prevAddress')
  const address = basket.get('address')

  if (slotId === '' && prevSlotId !== '') {
    dispatch({
      type: actionTypes.BASKET_SLOT_CHANGE,
      slotId: prevSlotId,
    })
  }

  if (postcode === '' && prevPostcode !== '') {
    dispatch({
      type: actionTypes.BASKET_POSTCODE_CHANGE,
      postcode: prevPostcode,
    })
  }

  if (address === null && prevAddress !== null) {
    dispatch({
      type: actionTypes.BASKET_ADDRESS_CHANGE,
      address: prevAddress,
    })
  }
}

export const basketRestorePreviousDate = () => (dispatch, getState) => {
  const { basket } = getState()
  const slotId = basket.get('prevSlotId')
  const date = basket.get('prevDate')
  dispatch(basketDateChange(date))
  dispatch({
    type: actionTypes.BASKET_SLOT_CHANGE,
    slotId,
  })

  dispatch(
    fetchData({ query: {}, params: {} }, true, undefined, {
      addRecipe: () => {},
    }),
  )

  dispatch({
    type: actionTypes.TRACKING_UNDO_DELIVERY_OPTIONS_CHANGE,
    trackingData: {
      actionType: trackingKeys.undoDeliveryOptionsChange,
    },
  })
}

export const basketCheckoutClicked = (section) => (dispatch, getState) => {
  const state = getState()
  const { basket } = state
  const recipes = basket.get('recipes')
  const menuId = basket.get('currentMenuId')
  const menuUuid = basket.get('currentMenuUuid')
  const transactionType = getTransactionType(state)
  const { promoCode, UTM } = getUTMAndPromoCode(state)
  dispatch({
    type: actionTypes.BASKET_CHECKOUT_CLICKED,
    trackingData: {
      actionType: trackingKeys.clickCheckout,
      ...UTM,
      promoCode,
      section,
      recipes,
      menu_id: menuId,
      menu_uuid: menuUuid,
      transaction_type: transactionType,
    },
  })
}

export const basketCheckedOut =
  ({ view, pricing }) =>
    (dispatch, getState) => {
      const state = getState()
      const isAuthenticated = getIsAuthenticated(state)
      const recipes = getBasketRecipes(state)
      const numRecipes = recipes.size

      try {
        dispatch(statusActions.pending(actionTypes.BASKET_CHECKOUT, true))

        if (isAuthenticated) {
          dispatch(trackingOrderCheckout({ pricing }))
        }

        dispatch({
          type: actionTypes.BASKET_CHECKOUT,
          trackingData: {
            actionType: trackingKeys.checkOutBasketAttempt,
            numRecipes,
            view,
          },
        })
      } catch (err) {
        dispatch(statusActions.error(actionTypes.BASKET_CHECKOUT, true))
      } finally {
        dispatch(statusActions.pending(actionTypes.BASKET_CHECKOUT, false))
      }
    }

export const basketProceedToCheckout = () => async (dispatch, getState) => {
  const { basket } = getState()
  dispatch({
    type: actionTypes.BASKET_CHECKOUT_PROCEED,
    trackingData: {
      actionType: trackingKeys.checkOutBasketComplete,
      basket,
      boxType: 'chosen',
    },
  })

  dispatch(statusActions.pending(actionTypes.BASKET_CHECKOUT, true))
  dispatch(statusActions.error(actionTypes.BASKET_CHECKOUT, false))

  try {
    dispatch(push(config.routes.client['check-out']))
  } catch (err) {
    dispatch(push(config.routes.client.menu))
    dispatch(statusActions.error(actionTypes.BASKET_CHECKOUT, err.message))
  } finally {
    dispatch(statusActions.pending(actionTypes.BASKET_CHECKOUT, false))
  }
}

export const basketSignupCollectionReceive = (collection) => ({
  type: actionTypes.BASKET_SIGNUP_COLLECTION_RECEIVE,
  collection,
})

export const basketSetSubscriptionOption = (subscriptionOption) => ({
  type: actionTypes.BASKET_SET_SUBSCRIPTION_OPTION,
  subscriptionOption,
})

export function basketEditModalOpen(
  selectedRecipes,
  maxNumberOfRecipes,
  numberOfPortionsPerBox,
) {
  return {
    type: actionTypes.TRACKING,
    trackingData: {
      actionType: basketChangesModalShown,
      selectedRecipes: selectedRecipes.map((recipe) => recipe.id),
      maxNumberOfRecipes,
      numberOfPortionsPerBox,
    },
  }
}

export function basketEditModalClosed(
  selectedRecipes,
  maxNumberOfRecipes,
  numberOfPortionsPerBox,
) {
  return {
    type: actionTypes.TRACKING,
    trackingData: {
      actionType: basketChangesModalClosed,
      selectedRecipes: selectedRecipes.map((recipe) => recipe.id),
      maxNumberOfRecipes,
      numberOfPortionsPerBox,
    },
  }
}

export function basketEditChangesConfirmed(
  selectedRecipes,
  removedRecipes,
  maxNumberOfRecipes,
  numberOfPortionsPerBox,
) {
  return {
    type: actionTypes.TRACKING,
    trackingData: {
      actionType: basketChangesConfirmed,
      selectedRecipes: selectedRecipes.map((recipe) => recipe.id),
      removedRecipes,
      maxNumberOfRecipes,
      numberOfPortionsPerBox,
    },
  }
}

export const basketSetIsFlexOrder = (isFlexOrder) => ({
  type: actionTypes.BASKET_IS_FLEX_ORDER,
  isFlexOrder,
})

export const actions = {
  basketOrderLoaded,
  basketDateChange,
  basketGiftAdd,
  setBasketNumPortion,
  basketNumPortionChange,
  portionSizeSelectedTracking,
  basketOrderLoad,
  basketOrderItemsLoad,
  basketProductAdd,
  basketProductRemove,
  basketPromoCodeChange,
  basketPromoCodeAppliedChange,
  basketPromoCodeUrlChange,
  basketPostcodeChangePure,
  basketPostcodeChange,
  basketPostcodeClear,
  basketAddressChange,
  basketRecipesInitialise,
  basketSlotChange,
  basketPreviewOrderChange,
  basketSlotClear,
  basketIdChange,
  basketTariffChange,
  basketChosenAddressChange,
  basketRestorePreviousValues,
  basketRestorePreviousDate,
  basketCheckedOut,
  basketCheckoutClicked,
  basketProceedToCheckout,
  basketReset,
  basketSignupCollectionReceive,
  basketSetSubscriptionOption,
  basketEditModalOpen,
  basketEditModalClosed,
  basketEditChangesConfirmed,
  basketSetIsFlexOrder,
}

// eslint-disable-next-line import/no-default-export
export default actions
